// Client-side PDF generation from an HTML node using html2canvas + jsPDF.
// Returns a single-page A4 portrait PDF as a Blob.

import html2canvas from "html2canvas";
import jsPDF from "jspdf";

/**
 * Generate a single-page A4 portrait PDF blob from a DOM node.
 * Tuned for small file size (~30-60 KB) while keeping text crisp:
 * scale 1.5 (still retina-ish), JPEG quality 0.72, jsPDF FAST compression.
 *
 * @param {HTMLElement} node - the element to render
 * @returns {Promise<Blob>}
 */
export async function generatePdfBlob(node) {
  if (!node) throw new Error("Node target tidak ditemukan");

  const canvas = await html2canvas(node, {
    scale: 1.5,
    backgroundColor: "#ffffff",
    useCORS: true,
    logging: false,
    windowWidth: node.scrollWidth,
    windowHeight: node.scrollHeight,
  });

  const imgData = canvas.toDataURL("image/jpeg", 0.72);
  const pdf = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4", compress: true });
  const pageW = pdf.internal.pageSize.getWidth();   // 210
  const pageH = pdf.internal.pageSize.getHeight();  // 297

  const imgRatio = canvas.height / canvas.width;
  let w = pageW;
  let h = w * imgRatio;
  if (h > pageH) {
    h = pageH;
    w = h / imgRatio;
  }
  const x = (pageW - w) / 2;
  const y = (pageH - h) / 2;
  pdf.addImage(imgData, "JPEG", x, y, w, h, undefined, "FAST");
  return pdf.output("blob");
}

/**
 * Trigger a browser download for a Blob with a given filename.
 */
export function downloadBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
